/**
 * RankingBarChart - Unified configurable bar chart for rankings
 * 
 * Consolidates: LocationVolumeChart, LocationMarginChart, PartTypeMarginChart
 * 
 * Supports:
 * - Vertical (default) or horizontal bar layout
 * - Configurable metric options with different formats (currency, percent, number)
 * - Sort toggling
 * - Customizable colors, height, max items
 */
import { useEffect, useMemo, useState } from 'react';
import { useAnimateOnDataChange } from '../shared/useAnimateOnDataChange';
import { 
    Box, Skeleton, Text, Flex, HStack, IconButton, Icon, 
    Button, Menu, MenuButton, MenuList, Tooltip
} from '@chakra-ui/react';
import { MenuItemRow } from '../../../ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, LabelList, Cell, ReferenceLine } from 'recharts';
import { RankingItem, NEGATIVE_METRICS, ReportingComparison } from '../../../types/reporting';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import { TbArrowUp, TbArrowDown, TbChevronDown, TbDownload, TbTrendingUp, TbTrendingDown, TbInfoCircle } from 'react-icons/tb';
import { exportToCsv } from '../../../utils/csvExport';
import { getBarChartYDomain, getBarChartYTicks, getNiceDomainMax } from '../../../utils/chartUtils';
import {
    chartValueFontSize,
    comparisonBarLabelListContent,
    stackedSegmentLabelListContent,
    verticalBarTopLabelListProps,
    yAxisTickFontSize,
    yAxisWidth,
} from '../shared/barValueLabels';
import { getSourceColor, getSourceLabel } from '../../../components/SourceBadge';
import { isStackedMarketplaceMetric } from '../widgets/types';
import { getRankingScrollbarGutterPx, getRankingXAxisLayout, needsRankingScrollbarGutter, RANKING_AXIS_TICK_MARGIN, truncateRankingAxisName } from '../shared/dateUtils';
import { MARKETPLACE_CHART_TOP_MARGIN, MarketplaceChartLegend } from '../shared/MarketplaceChartLegend';
import { useReportingChartFullscreen } from '../shared/reportingChartFullscreen';
import { useChartContainerWidth } from '../shared/useChartContainerWidth';
import { BAR_SERIES_COLORS, PRIMARY_CHART_COLOR } from '../shared/formatters';
import { compareMetricWithPriorTiebreaker } from '../../../hooks/reporting/reportingBundleDomainStats';

// Recharts dataKey prefix for per-marketplace series in breakdown mode.
const SOURCE_SERIES_PREFIX = 'src__';

// ============================================================================
// Types
// ============================================================================

export type MetricFormat = 'currency' | 'percent' | 'number';

export interface MetricOption {
    value: string;
    label: string;
    format: MetricFormat;
    tooltip?: string;
}

export interface RankingBarChartProps {
    /** Chart data */
    data: RankingItem[] | undefined;
    /** Loading state */
    isLoading: boolean;
    /** Chart title */
    title: string;
    /** Available metric options for the dropdown */
    metricOptions: MetricOption[];
    /** Default metric to display */
    defaultMetric?: string;
    /** Chart orientation */
    layout?: 'vertical' | 'horizontal';
    /** Chart height in pixels or string (e.g. "100%") */
    height?: number | string;
    /** Max items to display (for horizontal charts) */
    maxItems?: number;
    /** Custom color palette */
    colors?: string[];
    /** Char cap for horizontal-bar names only. Vertical bars use truncateRankingAxisName. */
    nameTruncateLength?: number;
    /** Comparison mode */
    comparison?: ReportingComparison;
    preset?: string;
    /** Minimum value for the axis (useful for percentage charts) */
    minValue?: number;
    /** Hide the internal metric selector (for external control) */
    hideControls?: boolean;
    /** Hides title/tooltip/download but keeps sort and metric selectors */
    hideTitle?: boolean;
    /** Controlled metric value (when hideControls is true) */
    controlledMetric?: string;
    /** Optional label formatter function */
    labelFormatter?: (value: string) => string;
    /** Optional drilldown click handler for a ranking item */
    onItemClick?: (item: RankingItem) => void;
    /** Show prior period bars alongside current bars when comparison data exists */
    showComparisonBars?: boolean;
    /** Initial sort order for hidden-control widgets */
    initialSortOrder?: 'asc' | 'desc';
    /** Cluster each entity's bar by marketplace (uses item.bySource) */
    marketplaceBreakdown?: boolean;
}

// ============================================================================
// Preset Metric Options (for convenience)
// ============================================================================

export const VOLUME_METRIC_OPTIONS: MetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
];

export const MARGIN_METRIC_OPTIONS: MetricOption[] = [
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
];

export const PART_TYPE_METRIC_OPTIONS: MetricOption[] = [
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best", divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'grossProfit', label: 'Total Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
];

// ============================================================================
// Formatters
// ============================================================================

const formatCompactCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000) {
        return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}$${(absValue / 1000).toFixed(1)}K`;
    }
    return `${sign}$${Math.round(absValue).toLocaleString()}`;
};

const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;

const formatCompactNumber = (value: number): string => {
    if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
    }
    return Math.round(value).toLocaleString();
};

const getFormatter = (format: MetricFormat): ((value: number) => string) => {
    switch (format) {
        case 'currency':
            return formatCompactCurrency;
        case 'percent':
            return formatPercent;
        case 'number':
        default:
            return formatCompactNumber;
    }
};

// Full formatter for tooltips (expanded precision)
const getTooltipFormatter = (format: MetricFormat): ((value: number) => string) => {
    switch (format) {
        case 'currency':
            return (value: number) => new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'USD', 
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        case 'percent':
            return (value: number) => `${(value || 0).toFixed(1)}%`;
        default:
            return (value: number) => value.toLocaleString();
    }
};

// ============================================================================
// Component
// ============================================================================

const CustomTooltip = ({ active, payload, label, metric, metricOption, preset }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const comparisonLabel = getSummaryComparisonLabel(preset) ?? 'vs prior';
        
        const formatter = getTooltipFormatter(metricOption.format);
        const value = data?.[metric] ?? 0;
        const priorValue =
            typeof data?.priorMetricValue === 'number'
                ? data.priorMetricValue
                : data?.prior?.[metric];
        
        let change: number | undefined;
        let changeLabel = '';
        
        if (data.change && data.change[metric] !== undefined) {
            change = data.change[metric];
            if (metricOption.format === 'percent') {
                changeLabel = 'pp'; 
            } else {
                changeLabel = '%';
            }
        }
        
        const isPositiveChange = change !== undefined && change >= 0;
        const IconComponent = isPositiveChange ? TbTrendingUp : TbTrendingDown;
        
        // Color Determination
        const isNegativeMetric = NEGATIVE_METRICS.includes(metric as any);
        let colorString = "gray.500";
        if (change !== undefined) {
            if (isNegativeMetric) {
                colorString = isPositiveChange ? "red.500" : "green.500";
            } else {
                colorString = isPositiveChange ? "green.500" : "red.500";
            }
        }
        
        return (
            <Box 
                bg="white" 
                p={3} 
                border="1px solid" 
                borderColor="gray.200" 
                borderRadius="lg" 
                boxShadow="none"
                minW="150px"
            >
                <Text fontSize="xs" color="gray.500" mb={1}>{data.fullName || label}</Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    {metricOption.label}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>
                    {formatter(value)}
                </Text>
                {priorValue !== undefined && priorValue !== null && (
                    <Text fontSize="xs" color="gray.500" mb={1}>
                        Prior: {formatter(priorValue)}
                    </Text>
                )}
                
                {change !== undefined && (
                    <Flex align="center" gap={1}>
                        <Icon 
                            as={IconComponent as React.ElementType} 
                            color={colorString} 
                            boxSize={3}
                        />
                        <Text 
                            fontSize="xs" 
                            fontWeight="medium" 
                            color={colorString}
                        >
                            {Math.abs(change).toFixed(1)}{changeLabel}
                            <Text as="span" color="gray.400" ml={1} fontWeight="normal">
                                {comparisonLabel}
                            </Text>
                        </Text>
                    </Flex>
                )}
                {change === undefined && priorValue !== undefined && priorValue !== null && (
                    <Text fontSize="xs" color="gray.400">—</Text>
                )}
            </Box>
        );
    }
    return null;
};

// Tooltip used in marketplace-breakdown mode: lists each marketplace's value
// for the hovered entity plus an overall total.
export const MarketplaceTooltip = ({ active, payload, metric, metricOption, sourceKeys, hasPrior, preset }: any) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const formatter = getTooltipFormatter(metricOption.format);
    const isPercent = metricOption.format === 'percent';
    const comparisonLabel = getSummaryComparisonLabel(preset) ?? 'vs prior';

    const entries = sourceKeys
        .map((src: string, i: number) => ({
            src,
            label: getSourceLabel(src),
            color: getSourceColor(src, i),
            value: Number(row[`${SOURCE_SERIES_PREFIX}${src}`]) || 0,
            priorValue: hasPrior && typeof row[`${SOURCE_SERIES_PREFIX}${src}__prior`] === 'number'
                ? row[`${SOURCE_SERIES_PREFIX}${src}__prior`] as number
                : undefined,
        }))
        .filter((e: any) => e.value !== 0 || e.priorValue !== undefined);

    // Prefer parent metric (distinct orders / true entity total). Summing bySource
    // overcounts when an RO touches multiple marketplaces; percent sums are meaningless.
    const total = typeof row[metric] === 'number'
        ? Number(row[metric])
        : entries.reduce((sum: number, e: any) => sum + e.value, 0);
    const totalLabel = metric === 'orderCount' ? 'Distinct Orders' : 'Total';
    const totalChange = typeof row.change?.[metric] === 'number' ? row.change[metric] as number : undefined;
    const changeLabel = isPercent ? 'pp' : '%';

    return (
        <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW={hasPrior ? '200px' : '180px'}>
            <Text fontSize="xs" color="gray.500" mb={1}>{row.fullName || row.displayName}</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1.5}>{metricOption.label}</Text>
            {entries.map((e: any) => (
                <Flex key={e.src} align="center" justify="space-between" gap={4} mb={0.5}>
                    <Flex align="center" gap={1.5}>
                        <Box w="8px" h="8px" borderRadius="full" bg={e.color} flexShrink={0} />
                        <Text fontSize="xs" color="gray.600">{e.label}</Text>
                    </Flex>
                    <Flex align="center" gap={1}>
                        {hasPrior && (
                            <>
                                <Text fontSize="xs" color="gray.400">
                                    {e.priorValue === undefined ? '—' : formatter(e.priorValue)}
                                </Text>
                                <Text fontSize="xs" color="gray.300">&rarr;</Text>
                            </>
                        )}
                        <Text fontSize="xs" fontWeight="medium" color="gray.800">{formatter(e.value)}</Text>
                    </Flex>
                </Flex>
            ))}
            {!isPercent && (
                <Flex align="center" justify="space-between" gap={4} mt={1.5} pt={1.5} borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.700">{totalLabel}</Text>
                    <Flex align="center" gap={1.5}>
                        <Text fontSize="xs" fontWeight="bold" color="#0071E3">{formatter(total)}</Text>
                        {totalChange !== undefined && isFinite(totalChange) && (
                            <Text fontSize="xs" fontWeight="medium" color={totalChange >= 0 ? 'green.500' : 'red.500'}>
                                {totalChange >= 0 ? '+' : ''}{totalChange.toFixed(1)}{changeLabel}
                                <Text as="span" color="gray.400" ml={0.5} fontWeight="normal">{comparisonLabel}</Text>
                            </Text>
                        )}
                        {hasPrior && totalChange === undefined && (
                            <Text fontSize="xs" color="gray.400">—</Text>
                        )}
                    </Flex>
                </Flex>
            )}
        </Box>
    );
};

const RankingBarChart = ({
    data,
    isLoading,
    title,
    metricOptions,
    defaultMetric,
    layout = 'vertical',
    height = 350,
    maxItems,
    colors = BAR_SERIES_COLORS,
    nameTruncateLength = 10,
    comparison,
    preset,
    minValue,
    hideControls = false,
    hideTitle = false,
    controlledMetric,
    labelFormatter,
    onItemClick,
    showComparisonBars = false,
    initialSortOrder = 'desc',
    marketplaceBreakdown = false,
}: RankingBarChartProps) => {
    // Detect if we have avg/median pairs
    const hasAvgMedianPairs = metricOptions.some(o => o.label.startsWith('Avg.')) && 
                              metricOptions.some(o => o.label.startsWith('Median'));

    // Determine initial aggregation type based on default metric
    const defaultMetricOption = metricOptions.find(o => o.value === (defaultMetric || metricOptions[0]?.value));
    const initialAggregationType: 'avg' | 'median' = 
        defaultMetricOption?.label.startsWith('Median') ? 'median' : 'avg';

    const [internalMetric, setInternalMetric] = useState<string>(defaultMetric || metricOptions[0]?.value || '');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
    const [aggregationType, setAggregationType] = useState<'avg' | 'median'>(initialAggregationType);
    const isFullscreenAxis = useReportingChartFullscreen();
    const [scrollWidthRef, scrollContainerWidth] = useChartContainerWidth();
    useEffect(() => {
        setSortOrder(initialSortOrder);
    }, [initialSortOrder]);

    // Use controlled metric if provided, otherwise use internal state
    const metric = hideControls && controlledMetric ? controlledMetric : internalMetric;
    const setMetric = hideControls ? () => {} : setInternalMetric;

    // Check if the currently selected metric has a corresponding avg/median version
    const currentMetricOption = metricOptions.find(o => o.value === metric);
    const hasCorrespondingPair = hasAvgMedianPairs && currentMetricOption && (
        currentMetricOption.label.startsWith('Avg.') || currentMetricOption.label.startsWith('Median')
    );

    // Filter metrics based on aggregation type if pairs exist
    const filteredMetricOptions = hasAvgMedianPairs 
        ? metricOptions.filter(o => {
            if (aggregationType === 'avg') {
                return o.label.startsWith('Avg.') || (!o.label.startsWith('Median') && !o.label.startsWith('Avg.'));
            } else {
                return o.label.startsWith('Median') || (!o.label.startsWith('Median') && !o.label.startsWith('Avg.'));
            }
        })
        : metricOptions;

    // Remove "Avg." or "Median" prefix from labels for display
    const displayMetricOptions = filteredMetricOptions.map(o => ({
        ...o,
        displayLabel: o.label.replace(/^(Avg\.|Median)\s*/, '')
    }));

    const metricOption = metricOptions.find(o => o.value === metric) || metricOptions[0];
    const formatter = getFormatter(metricOption?.format || 'number');

    const chartData = useMemo(() => {
        if (!data?.length) return [];
        let sortedData = [...data]
            .filter(item => item && item.name)
            .sort((a, b) => compareMetricWithPriorTiebreaker(
                a,
                b,
                metric,
                sortOrder,
                showComparisonBars,
            ));
        if (maxItems && maxItems > 0) {
            sortedData = sortedData.slice(0, maxItems);
        }
        return sortedData.map(item => {
            const fullName = labelFormatter ? labelFormatter(item.name) : item.name;
            const displayName = layout === 'horizontal'
                ? (fullName.length > nameTruncateLength
                    ? `${fullName.substring(0, nameTruncateLength)}...`
                    : fullName)
                : truncateRankingAxisName(fullName, isFullscreenAxis);
            return {
                ...item,
                priorMetricValue:
                    typeof item.prior?.[metric as keyof typeof item.prior] === 'number'
                        ? Number(item.prior?.[metric as keyof typeof item.prior])
                        : undefined,
                displayName,
                fullName: fullName
            };
        });
    }, [data, metric, sortOrder, maxItems, labelFormatter, layout, nameTruncateLength, isFullscreenAxis, showComparisonBars]);

    const animateBars = useAnimateOnDataChange(data);

    if (isLoading && (!data || data.length === 0)) {
        return (
            <Flex align="center" justify="center" w="100%" h="100%" p={2}>
                <Skeleton height="100%" width="100%" borderRadius="lg" />
            </Flex>
        );
    }

    if (!data || data.length === 0 || chartData.length === 0) {
        return (
            <Box h={typeof height === 'number' ? `${height}px` : height} display="flex" alignItems="center" justifyContent="center">
                <Text color="gray.500">No data available</Text>
            </Box>
        );
    }

    // ---- Marketplace breakdown ----------------------------------------------
    const useMarketplace =
        marketplaceBreakdown && chartData.some((d: any) => d.bySource && Object.keys(d.bySource).length > 0);

    // Additive metrics (order count/volume/profit) stack; ratios cluster.
    const isStacked = useMarketplace && isStackedMarketplaceMetric(metric);

    // Overlay the prior-period marketplace breakdown when prior bars are enabled
    // and the backend provided per-source prior data.
    const showMarketplacePrior =
        useMarketplace &&
        showComparisonBars &&
        chartData.some((d: any) => {
            const bs = d.bySource as Record<string, any> | undefined;
            return bs ? Object.values(bs).some((m: any) => typeof m?.prior?.[metric] === 'number') : false;
        });

    // Union of marketplace sources across visible entities, ordered by total
    // contribution so the most significant marketplaces lead the cluster.
    const sourceTotals = new Map<string, number>();
    if (useMarketplace) {
        for (const d of chartData) {
            const bs = (d as any).bySource as Record<string, any> | undefined;
            if (!bs) continue;
            for (const [src, m] of Object.entries(bs)) {
                // Include prior so prior-only marketplaces (current 0) still rank/show.
                const v = (Number((m as any)?.[metric]) || 0)
                    + (Number((m as any)?.prior?.[metric]) || 0);
                sourceTotals.set(src, (sourceTotals.get(src) || 0) + Math.abs(v));
            }
        }
    }
    const sourceKeys = Array.from(sourceTotals.entries()).sort((a, b) => b[1] - a[1]).map(([s]) => s);

    // Flatten per-source metric values into chart-friendly keys (src__<source>).
    const renderData: any[] = useMarketplace
        ? chartData.map((d: any) => {
            const row: any = { ...d };
            const bs = d.bySource as Record<string, any> | undefined;
            for (const src of sourceKeys) {
                row[`${SOURCE_SERIES_PREFIX}${src}`] = Number(bs?.[src]?.[metric]) || 0;
                if (showMarketplacePrior) {
                    const priorSourceValue = bs?.[src]?.prior?.[metric];
                    row[`${SOURCE_SERIES_PREFIX}${src}__prior`] =
                        typeof priorSourceValue === 'number' ? priorSourceValue : undefined;
                }
            }
            return row;
        })
        : chartData;

    const hasComparisonBarData =
        !useMarketplace &&
        showComparisonBars && chartData.some((d) => typeof d.priorMetricValue === 'number' && !isNaN(d.priorMetricValue));

    let dataMin = 0;
    let dataMax = 0;
    if (useMarketplace) {
        // Stacked metrics size the axis to the stack total; clustered to the tallest bar.
        const accumulate = (vals: number[]) => {
            if (isStacked) {
                const positiveSum = vals.filter((v) => v > 0).reduce((a, b) => a + b, 0);
                const negativeSum = vals.filter((v) => v < 0).reduce((a, b) => a + b, 0);
                dataMax = Math.max(dataMax, positiveSum);
                dataMin = Math.min(dataMin, negativeSum);
            } else {
                vals.forEach((v) => {
                    dataMax = Math.max(dataMax, v);
                    dataMin = Math.min(dataMin, v);
                });
            }
        };
        renderData.forEach((row: any) => {
            accumulate(sourceKeys.map((src) => Number(row[`${SOURCE_SERIES_PREFIX}${src}`]) || 0));
            if (showMarketplacePrior) {
                accumulate(sourceKeys.flatMap((src) => {
                    const value = row[`${SOURCE_SERIES_PREFIX}${src}__prior`];
                    return typeof value === 'number' ? [value] : [];
                }));
            }
        });
    } else {
        const values = chartData
            .flatMap((d) => [
                (d as any)[metric] || 0,
                ...(hasComparisonBarData && typeof d.priorMetricValue === 'number' ? [d.priorMetricValue] : []),
            ])
            .filter(v => !isNaN(v));
        dataMin = values.length ? Math.min(...values) : 0;
        dataMax = values.length ? Math.max(...values) : 0;
    }

    const isHorizontal = layout === 'horizontal';
    const isPercent = metricOption?.format === 'percent';
    const formatForDomain = metricOption?.format || 'number';

    let axisMin: number;
    let axisMax: number;

    const rankingAxis = getRankingXAxisLayout(
        chartData.map((d: any) => d.displayName || ''),
        isFullscreenAxis,
    );
    const axisBand = rankingAxis.height;
    const axisMarginBottom = rankingAxis.bottom;
    const verticalAxisFontSize = rankingAxis.fontSize;

    // Vertical plot pixel height (chart height minus top/bottom margins and the x-axis band),
    // used to guarantee a constant pixel gap below negative bars. Fullscreen ('flex') height isn't
    // known synchronously, so estimate from the viewport (the plot is large there anyway).
    const verticalPlotHeightPx = typeof height === 'string'
        ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
        : Math.max(0, height - MARKETPLACE_CHART_TOP_MARGIN - axisMarginBottom - axisBand);

    if (isHorizontal) {
        // Bars start flush against labels; value labels on each bar make axis ticks redundant
        axisMin = dataMin;
        axisMax = getNiceDomainMax(dataMax, 1.08);
    } else {
        [axisMin, axisMax] = getBarChartYDomain(dataMin, dataMax, {
            format: formatForDomain,
            capPercentMaxAt100: false,
        }, verticalPlotHeightPx);
        if (minValue !== undefined && dataMin >= minValue) {
            axisMin = Math.max(axisMin, minValue);
        }
    }

    const axisDomain: [number, number] = [axisMin, axisMax];

    const isFlexHeight = typeof height === 'string';
    const heightNum = typeof height === 'number' ? height : 0;

    // Dynamic height for horizontal charts based on item count
    const dynamicHeight = isHorizontal
        ? Math.max(heightNum ? heightNum - 60 : 300, chartData.length * 32)
        : height;
        
    // Dynamic width for vertical charts based on item count to allow horizontal scrolling.
    // In marketplace mode each category holds a cluster of source bars, so widen per category.
    const minBarWidth = useMarketplace
        ? (isStacked
            ? (showMarketplacePrior ? 96 : 56)
            : Math.max(56, sourceKeys.length * (showMarketplacePrior ? 30 : 18) + 24))
        : hasComparisonBarData ? 52 : 40;
    const dynamicWidth = isHorizontal 
        ? '100%' 
        : Math.max(chartData.length * minBarWidth, 100);
    const showScrollbarGutter = !isHorizontal
        && typeof dynamicWidth === 'number'
        && needsRankingScrollbarGutter(dynamicWidth, scrollContainerWidth);
    const rankingValueFont = chartValueFontSize(isFullscreenAxis, isHorizontal || !showScrollbarGutter);
    const rankingYAxisFont = yAxisTickFontSize(isFullscreenAxis, 10);
    const axisWidth = yAxisWidth(isFullscreenAxis);

    const marketplaceTooltip = (
        <RechartsTooltip
            content={<MarketplaceTooltip metric={metric} metricOption={metricOption} sourceKeys={sourceKeys} hasPrior={showMarketplacePrior} preset={preset} />}
            cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
        />
    );

    // Source bars for marketplace rendering (shared by both orientations). When
    // prior is enabled, all prior bars render first (faded, preceding current) —
    // for stacked metrics this yields a faded prior stack then the current stack.
    const sourceBarRadius = (vertical: boolean, isLast: boolean): [number, number, number, number] => {
        if (isStacked) {
            return vertical
                ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0])
                : (isLast ? [0, 4, 4, 0] : [0, 0, 0, 0]);
        }
        return vertical ? [4, 4, 0, 0] : [0, 4, 4, 0];
    };
    const renderSourceBars = (vertical: boolean) => {
        const bars: JSX.Element[] = [];
        const marketplaceLabelProps = (dataKey: string, isPrior: boolean) => {
            if (isStacked) {
                return {
                    dataKey,
                    content: stackedSegmentLabelListContent(formatter, rankingValueFont),
                };
            }
            if (vertical) {
                return {
                    dataKey,
                    ...(isPrior || showMarketplacePrior
                        ? { content: comparisonBarLabelListContent(formatter, rankingValueFont, true) }
                        : verticalBarTopLabelListProps(formatter, rankingValueFont, true)),
                };
            }
            return {
                dataKey,
                position: 'right' as const,
                formatter,
                fill: '#4A5568',
                fontSize: rankingValueFont,
                fontWeight: 'bold' as const,
            };
        };
        if (showMarketplacePrior) {
            sourceKeys.forEach((src, i) => {
                const priorDataKey = `${SOURCE_SERIES_PREFIX}${src}__prior`;
                bars.push(
                    <Bar
                        key={`${src}__prior`}
                        dataKey={priorDataKey}
                        name={`${getSourceLabel(src)} (prior)`}
                        legendType="none"
                        fill={getSourceColor(src, i)}
                        fillOpacity={0.4}
                        stackId={isStacked ? 'mp_prior' : undefined}
                        radius={sourceBarRadius(vertical, i === sourceKeys.length - 1)}
                        isAnimationActive={animateBars}
                    >
                        <LabelList {...marketplaceLabelProps(priorDataKey, true)} />
                    </Bar>
                );
            });
        }
        sourceKeys.forEach((src, i) => {
            const dataKey = `${SOURCE_SERIES_PREFIX}${src}`;
            bars.push(
                <Bar
                    key={src}
                    dataKey={dataKey}
                    name={getSourceLabel(src)}
                    fill={getSourceColor(src, i)}
                    stackId={isStacked ? 'mp' : undefined}
                    radius={sourceBarRadius(vertical, i === sourceKeys.length - 1)}
                    isAnimationActive={animateBars}
                >
                    {renderData.map((entry, idx) => (
                        <Cell
                            key={`${src}-cell-${idx}`}
                            cursor={onItemClick ? 'pointer' : undefined}
                            onClick={() => onItemClick?.(entry)}
                        />
                    ))}
                    <LabelList {...marketplaceLabelProps(dataKey, false)} />
                </Bar>
            );
        });
        return bars;
    };

    const handleDownload = () => {
        if (!chartData) return;
        exportToCsv(chartData, `${title.replace(/\s+/g, '_').toLowerCase()}_data`);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            height={isFlexHeight ? height : undefined}
            flex={isFlexHeight ? 1 : undefined}
            minH={isFlexHeight ? 0 : undefined}
            minW={0}
            w="100%"
        >
            {!hideControls && (
                <Flex align="center" mb={3} gap={2} justify={hideTitle ? 'flex-end' : 'space-between'} bg="gray.50" borderRadius="md" px={3} py={2}>
                    {!hideTitle && (
                        <Flex align="center" gap={2}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.600" whiteSpace="nowrap">
                                {hasCorrespondingPair 
                                    ? metricOption.label.replace(/^(Avg\.|Median)\s*/, '').replace(/\s*\([^)]+\)\s*/g, '')
                                    : metricOption.label.replace(/\s*\([^)]+\)\s*/g, '')}
                            </Text>
                            {metricOption?.tooltip && (
                                <Tooltip label={metricOption.tooltip} placement="top" hasArrow>
                                    <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help">
                                        <Icon as={TbInfoCircle as React.ElementType} boxSize={4} />
                                    </Box>
                                </Tooltip>
                            )}
                            <IconButton
                                aria-label="Download CSV"
                                icon={<Icon as={TbDownload as React.ElementType} />}
                                size="sm"
                                h="32px"
                                minW="32px"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'gray.600', bg: 'gray.100' }}
                                onClick={handleDownload}
                            />
                        </Flex>
                    )}
                    <HStack spacing={2} flexShrink={0}>
                    <IconButton
                        aria-label="Sort order"
                        icon={sortOrder === 'desc'
                            ? <Icon as={TbArrowDown as React.ElementType} />
                            : <Icon as={TbArrowUp as React.ElementType} />}
                        size="sm"
                        h="32px"
                        variant="outline"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        bg="white"
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                    />
                    {/* Aggregation Type Dropdown (Avg/Median) - shown only when current metric has a pair */}
                    {hasCorrespondingPair && (
                        <Menu placement="bottom-start">
                            <MenuButton
                                as={Button}
                                variant="outline"
                                bg="white"
                                borderColor="gray.300"
                                borderRadius="md"
                                fontWeight="normal"
                                size="sm"
                                h="32px"
                                px={2}
                                w="75px"
                                textAlign="left"
                                rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                                _hover={{ borderColor: 'gray.400' }}
                                _active={{ bg: 'white' }}
                            >
                                <Text fontSize="xs" noOfLines={1}>
                                    {aggregationType === 'avg' ? 'Avg.' : 'Median'}
                                </Text>
                            </MenuButton>
                            <MenuList p="6px" minW="100px" zIndex={2000}>
                                <MenuItemRow
                                    kind="single"
                                    isSelected={aggregationType === 'avg'}
                                    onClick={() => {
                                        setAggregationType('avg');
                                        const currentOption = metricOptions.find(o => o.value === metric);
                                        if (currentOption?.label.startsWith('Median')) {
                                            const avgLabel = currentOption.label.replace('Median', 'Avg.');
                                            const avgVersion = metricOptions.find(o => o.label === avgLabel);
                                            if (avgVersion) setMetric(avgVersion.value);
                                        }
                                    }}
                                >
                                    Avg.
                                </MenuItemRow>
                                <MenuItemRow
                                    kind="single"
                                    isSelected={aggregationType === 'median'}
                                    onClick={() => {
                                        setAggregationType('median');
                                        const currentOption = metricOptions.find(o => o.value === metric);
                                        if (currentOption?.label.startsWith('Avg.')) {
                                            const medianLabel = currentOption.label.replace('Avg.', 'Median');
                                            const medianVersion = metricOptions.find(o => o.label === medianLabel);
                                            if (medianVersion) setMetric(medianVersion.value);
                                        }
                                    }}
                                >
                                    Median
                                </MenuItemRow>
                            </MenuList>
                        </Menu>
                    )}
                    {/* Menu-style dropdown matching global style */}
                    <Menu placement="bottom-end">
                        <MenuButton
                            as={Button}
                            variant="outline"
                            bg="white"
                            borderColor="gray.300"
                            borderRadius="md"
                            fontWeight="normal"
                            size="sm"
                            h="32px"
                            px={2}
                            w="130px"
                            textAlign="left"
                            rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                            _hover={{ borderColor: 'gray.400' }}
                            _active={{ bg: 'white' }}
                        >
                            <Text fontSize="xs" noOfLines={1}>
                                {hasCorrespondingPair 
                                    ? metricOption?.label.replace(/^(Avg\.|Median)\s*/, '') || 'Select metric'
                                    : metricOption?.label || 'Select metric'}
                            </Text>
                        </MenuButton>
                        <MenuList p="6px" minW="180px" zIndex={2000} maxH="300px" overflowY="auto">
                            {displayMetricOptions.map(option => {
                                const isSelected = option.value === metric;
                                return (
                                    <MenuItemRow
                                        key={option.value}
                                        kind="single"
                                        isSelected={isSelected}
                                        onClick={() => setMetric(option.value)}
                                    >
                                        {hasAvgMedianPairs ? option.displayLabel : option.label}
                                        {option.tooltip ? (
                                            <Tooltip label={option.tooltip} placement="right" hasArrow>
                                                <Box as="span" display="inline-flex" ml={2} verticalAlign="middle">
                                                    <Icon as={TbInfoCircle as React.ElementType} boxSize={3} />
                                                </Box>
                                            </Tooltip>
                                        ) : null}
                                    </MenuItemRow>
                                );
                            })}
                        </MenuList>
                    </Menu>
                    </HStack>
                </Flex>
            )}

            {isHorizontal ? (
                // Horizontal bar chart (bars go left-to-right)
                <Box
                    position="relative"
                    maxH={isFlexHeight ? '100%' : `${height}px`}
                    flex={isFlexHeight ? 1 : undefined}
                    minH={isFlexHeight ? 0 : undefined}
                >
                    {useMarketplace && <MarketplaceChartLegend sourceKeys={sourceKeys} isFullscreen={isFullscreenAxis} />}
                    <Box
                        overflowY="auto"
                        maxH="100%"
                        h="100%"
                        pr={2}
                    >
                    <ResponsiveContainer width="100%" height={dynamicHeight}>
                        <BarChart
                            data={renderData}
                            layout="vertical"
                            margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 58, left: 0, bottom: 5 }}
                            barGap={useMarketplace ? 2 : 4}
                            barCategoryGap={hasComparisonBarData ? '20%' : '30%'}
                        >
                        <CartesianGrid stroke="none" />
                        <XAxis
                            type="number"
                            domain={axisDomain}
                            hide
                        />
                        <YAxis
                            type="category"
                            dataKey="displayName"
                            tick={{ fontSize: 10, fill: '#4A5568' }}
                            tickLine={false}
                            axisLine={false}
                            width={120}
                        />
                                {useMarketplace ? marketplaceTooltip : (
                                    <RechartsTooltip
                                        content={<CustomTooltip metric={metric} metricOption={metricOption} preset={preset} showComparisonBars={hasComparisonBarData} />}
                                        cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                                    />
                                )}
                                {axisMin < 0 && <ReferenceLine x={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                                {useMarketplace ? renderSourceBars(false) : (
                                <>
                                {hasComparisonBarData && (
                                    <Bar dataKey="priorMetricValue" radius={[0, 4, 4, 0]} fill="#CBD5E0" fillOpacity={0.9} isAnimationActive={animateBars}>
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`prior-cell-${index}`}
                                                fill="#CBD5E0"
                                                cursor={onItemClick ? 'pointer' : undefined}
                                                onClick={() => onItemClick?.(entry)}
                                            />
                                        ))}
                                    </Bar>
                                )}
                                <Bar dataKey={metric} radius={[0, 4, 4, 0]} isAnimationActive={animateBars}>
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={PRIMARY_CHART_COLOR}
                                    cursor={onItemClick ? 'pointer' : undefined}
                                    onClick={() => onItemClick?.(entry)}
                                />
                            ))}
                            <LabelList
                                dataKey={metric}
                                position="right"
                                formatter={formatter}
                                fill="#4A5568"
                                fontSize={rankingValueFont}
                                fontWeight="bold"
                            />
                        </Bar>
                        </>
                        )}
                    </BarChart>
                </ResponsiveContainer>
                    </Box>
                </Box>
            ) : (
                // Vertical bar chart (bars go bottom-to-top). Legend sits on the
                // visible card; only the plot scrolls horizontally.
                <Box
                    position="relative"
                    w="100%"
                    minW={0}
                    height={isFlexHeight ? undefined : `${height}px`}
                    flex={isFlexHeight ? 1 : undefined}
                    minH={isFlexHeight ? 0 : undefined}
                >
                    {useMarketplace && <MarketplaceChartLegend sourceKeys={sourceKeys} isFullscreen={isFullscreenAxis} />}
                    <Box
                        ref={scrollWidthRef}
                        overflowX="auto"
                        overflowY="hidden"
                        w="100%"
                        minW={0}
                        height="100%"
                        pb={showScrollbarGutter ? `${getRankingScrollbarGutterPx()}px` : 0}
                    >
                    <Box minWidth={typeof dynamicWidth === 'number' ? `${dynamicWidth}px` : dynamicWidth} width="100%" height="100%">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={renderData}
                                margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 25, left: 0, bottom: axisMarginBottom }}
                                barGap={useMarketplace ? 2 : 4}
                                barCategoryGap={hasComparisonBarData ? '20%' : '30%'}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis
                                    dataKey="displayName"
                                    tick={{ fontSize: verticalAxisFontSize, fill: '#4A5568' }}
                                    tickLine={false}
                                    axisLine={{ stroke: '#E2E8F0' }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={axisBand}
                                    interval={0}
                                    tickMargin={RANKING_AXIS_TICK_MARGIN}
                                />
                                <YAxis
                                    domain={axisDomain}
                                    ticks={getBarChartYTicks(axisDomain[0], axisDomain[1])}
                                    tick={{ fontSize: rankingYAxisFont, fill: '#718096' }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v) => (v === 0 && axisDomain[0] >= 0 ? '' : formatter(v))}
                                    width={axisWidth}
                                    interval={0}
                                    allowDecimals={isPercent}
                                />
                                {useMarketplace ? marketplaceTooltip : (
                                    <RechartsTooltip
                                        content={<CustomTooltip metric={metric} metricOption={metricOption} preset={preset} showComparisonBars={hasComparisonBarData} />}
                                        cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                                    />
                                )}
                                {axisMin < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                                {useMarketplace && renderSourceBars(true)}
                                {!useMarketplace && hasComparisonBarData && (
                                    <Bar dataKey="priorMetricValue" radius={[4, 4, 0, 0]} fill="#CBD5E0" fillOpacity={0.9} isAnimationActive={animateBars}>
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`prior-cell-${index}`}
                                                fill="#CBD5E0"
                                                cursor={onItemClick ? 'pointer' : undefined}
                                                onClick={() => onItemClick?.(entry)}
                                            />
                                        ))}
                                        <LabelList
                                            dataKey="priorMetricValue"
                                            content={comparisonBarLabelListContent(formatter, rankingValueFont)}
                                        />
                                    </Bar>
                                )}
                                {!useMarketplace && (
                                <Bar dataKey={metric} radius={[4, 4, 0, 0]} isAnimationActive={animateBars}>
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={PRIMARY_CHART_COLOR}
                                            cursor={onItemClick ? 'pointer' : undefined}
                                            onClick={() => onItemClick?.(entry)}
                                        />
                                    ))}
                                    <LabelList
                                        dataKey={metric}
                                        {...(hasComparisonBarData
                                            ? { content: comparisonBarLabelListContent(formatter, rankingValueFont) }
                                            : verticalBarTopLabelListProps(formatter, rankingValueFont))}
                                    />
                                </Bar>
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default RankingBarChart;
